import { useCallback, useEffect, useRef } from "react";
import { useWebSocket } from "./useWebSocket.js";
import { useVoicePreferences, isTypeAnnounced } from "./useVoicePreferences.js";
import { useAudioUnlock } from "./useAudioUnlock.js";
import { alertSpeech } from "@/lib/alertSpeech.js";
import { playEarcon } from "@/lib/earcons.js";
import {
  announcementKey,
  claimAnnouncement,
  claimSummary,
  confirmAnnouncement,
  releaseAnnouncement,
  releaseAnnouncementNow,
  HIDDEN_CLAIM_DELAY_MS,
  PROVISIONAL_TTL_MS,
} from "@/lib/voiceLedger.js";
import {
  speakText,
  cancelSpeech,
  isSpeechSupported,
  publishVoicePreferences,
  waitForSpeechIdle,
  needsCrossTabCoordination,
  speechCancelEpoch,
  speechClaimMs,
} from "@/lib/speech.js";
import { useAlertUrgency } from "./useAlertUrgency.js";

/**
 * useVoiceAnnouncements — locuta las alertas que llegan por WebSocket.
 *
 * Alcance real, sin ambigüedad: esto habla mientras la app está ABIERTA. Con
 * la pestaña cerrada y el teléfono bloqueado la alerta la entrega el Service
 * Worker, y un Service Worker no tiene acceso a `Audio` ni a
 * `speechSynthesis` — la Notification API tampoco permite fijar un sonido
 * propio. Locutar con la pantalla apagada exige una app nativa; acá el
 * objetivo es el puesto de monitoreo, que es donde alguien está mirando.
 *
 * Diseño de la cola:
 *  - Estrictamente secuencial. Dos locuciones simultáneas no se entienden.
 *  - Tope de `MAX_QUEUE`. Una ráfaga de 30 alertas serían ~90 s hablando
 *    encima de eventos ya viejos; se locutan las primeras y el resto se
 *    resume con un "y N alertas más".
 *  - Cooldown por (vehículo, tipo): el mismo evento repitiéndose no se locuta
 *    dos veces seguidas aunque el backend sí lo registre.
 *  - Una reclamación COMPARTIDA entre pestañas antes de hablar. Las tres
 *    defensas de arriba viven en refs de React, o sea dentro de UNA pestaña:
 *    con Monitor abierto en una y Reportes en otra —la forma normal de trabajar
 *    en un puesto— cada una locutaba el mismo SOS por su cuenta y se escuchaba
 *    dos veces. Ver `lib/voiceLedger.js`.
 */

// Cuántas alertas se guardan para locutar antes de empezar a resumir.
const MAX_QUEUE = 4;
// Mismo vehículo + mismo tipo: no repetir la locución dentro de esta ventana.
const REPEAT_COOLDOWN_MS = 60_000;
// Tope del mapa de cooldowns — una flota grande con muchos tipos podría
// crecerlo sin límite en una sesión larga.
const MAX_COOLDOWN_ENTRIES = 500;
// Margen sobre el vencimiento de la reclamación provisional antes de que la
// pestaña que cedió el turno vuelva a intentarlo. Ver `cedidosRef`.
const RETRY_SLACK_MS = 500;
// Cuántas veces se reintenta una alerta que otra pestaña se llevó. Ver el
// `continue` del drain: con una sola vez, la cedida que llega tarde a la tanda
// se perdía.
const MAX_REINTENTOS = 2;
// Techo del contador de descartadas. "y 200 alertas más" y "y 4.000 alertas
// más" dicen lo mismo, y sin tope una pestaña de fondo lo hace crecer sin
// límite.
const MAX_DROPPED = 200;
// Cuánto vive la cuenta de descartadas sin poder decirse. Pasado el plazo, un
// "y N alertas más" habla de eventos que ya nadie va a atender.
const DROPPED_TTL_MS = 10 * 60_000;

export function useVoiceAnnouncements() {
  const { prefs } = useVoicePreferences();
  // Qué tipos son urgentes lo decide la flota (Control → Alertas), no una
  // constante del código: es lo mismo que elige el canal de Android, y la voz
  // tiene que hablar por el mismo lado que suena el tono. Mientras la respuesta
  // no llegó, `isUrgent` cae al default de la plataforma.
  const { isUrgent } = useAlertUrgency();
  const isUrgentRef = useRef(isUrgent);
  isUrgentRef.current = isUrgent;

  // Los prefs viven en un ref además del estado: el handler del WebSocket se
  // registra una sola vez y leería los valores del primer render para siempre.
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  const queueRef = useRef([]);
  // Alertas cuyo turno se llevó otra pestaña. NO se tiran: esa pestaña puede no
  // llegar a hablar (audio bloqueado, sintetizador que falla, pestaña que se
  // cierra), y entonces su reclamación provisional vence y esta las toma.
  // `item.intentos` cuenta cuántas vueltas lleva; agotadas, se cuenta para el
  // resumen en vez de desaparecer.
  const cedidosRef = useRef([]);
  const retryRef = useRef(null);
  // La alerta que esta pestaña tiene reclamada y TODAVÍA no terminó de decir.
  // Existe para el cierre de la pestaña: ver el efecto de `pagehide`.
  const enVueloRef = useRef(null);
  const draining = useRef(false);
  const droppedRef = useRef(0);
  // Cuándo se anotó la última descartada: la cuenta caduca (ver `drain`).
  const droppedAtRef = useRef(0);
  const lastSpokenRef = useRef(new Map());
  const seenIdsRef = useRef(new Set());
  const mountedRef = useRef(true);

  // ¿Hay que callarse ya? Se consulta ANTES de cada await que produce sonido,
  // no una sola vez por vuelta: entre el earcon y la frase pasan cientos de
  // milisegundos, y el usuario puede apagar la voz justo ahí.
  const debeCallarse = useCallback(() => {
    if (prefsRef.current.enabled && mountedRef.current) return false;
    queueRef.current = [];
    cedidosRef.current = [];
    droppedRef.current = 0;
    // El temporizador también: dejándolo armado, `!retryRef.current` bloquea
    // programar uno nuevo hasta que el viejo dispare, así que la primera tanda
    // de cedidas después de volver a encender la voz no se reintentaba.
    clearTimeout(retryRef.current);
    retryRef.current = null;
    return true;
  }, []);

  /**
   * Tira la cuenta de descartadas si ya es vieja.
   *
   * Una pestaña con el audio todavía bloqueado no dice nada y acumula: sin
   * esto, la cuenta sobrevivía hasta que alguien hiciera el primer click y el
   * puesto escuchaba "y 200 alertas más" —topeado, además— detrás de la primera
   * alerta que sí sonó, por eventos de hacía horas. Un resumen viejo no
   * informa: desorienta.
   */
  const caducarDescartadas = useCallback(() => {
    if (droppedRef.current > 0 && Date.now() - droppedAtRef.current > DROPPED_TTL_MS) {
      droppedRef.current = 0;
      droppedAtRef.current = 0;
    }
  }, []);

  /** Una alerta que no se va a decir, pero sí a contar en el resumen. */
  const anotarDescartada = useCallback((cuantas = 1) => {
    // La caducidad se aplica ACÁ, que es el único lugar por donde la cuenta
    // sube. Evaluándola al entrar al drain, una descartada anotada por
    // `handleMessage` —la cola llena, el excedente de una ráfaga— entraba a una
    // tanda vencida y el drain se la llevaba puesta dos líneas después, sin
    // haber vivido ni un tick.
    caducarDescartadas();
    // El instante es el de la PRIMERA descartada de la tanda, no el de la
    // última: pisándolo en cada una, el plazo medía inactividad en vez de
    // antigüedad, y justo el caso que la caducidad viene a cortar —una pestaña
    // muda mientras la flota genera alertas sin parar— lo refrescaba para
    // siempre y nunca caducaba.
    if (droppedRef.current === 0) droppedAtRef.current = Date.now();
    droppedRef.current = Math.min(droppedRef.current + cuantas, MAX_DROPPED);
  }, [caducarDescartadas]);

  const drain = useCallback(async () => {
    if (draining.current) return;
    draining.current = true;
    caducarDescartadas();
    // ¿Esta pestaña llegó a ser la que anuncia en esta vuelta? Decide si le
    // corresponde también el resumen de abajo: sin esto, dos pestañas que
    // desbordan la cola con la misma ráfaga dicen las dos "y 3 alertas más".
    let hablo = false;
    // Alertas cuyo turno ganó esta pestaña y que igual no sonaron. No son de
    // nadie más, así que la cuenta de descartadas no puede vaciarse encima.
    let noHablaron = 0;
    try {
      while (queueRef.current.length > 0) {
        const item = queueRef.current.shift();
        const { volume, rate, voiceURI, earcons } = prefsRef.current;

        // Releer `enabled` en cada vuelta: si el usuario apaga la voz a mitad
        // de una ráfaga, la cola debe callarse ya, no terminar de vaciarse.
        if (debeCallarse()) break;

        // Ponerse de acuerdo con las otras pestañas ANTES de hacer el primer
        // ruido. Va acá y no al encolar por dos motivos: el tono no puede sonar
        // en una pestaña que después se va a callar, y reclamar en el momento
        // de hablar deja ganar a la pestaña que puede hablar YA en vez de a la
        // que recibió el WebSocket primero y todavía tiene cola por delante.
        //
        // Una pestaña OCULTA le cede la ventaja a una visible: la que el
        // usuario está mirando es la que con seguridad tiene el audio
        // desbloqueado. Si todas están ocultas nadie queda mudo, solo se habla
        // un cuarto de segundo más tarde.
        const coordinar = needsCrossTabCoordination();
        // El sintetizador del navegador es UNO por documento, y la frase de
        // prueba de los ajustes («Probar», en Anuncios por voz) habla por él sin
        // pasar por el registro compartido. Reclamando con esa frase sonando,
        // la alerta quedaba encolada detrás y empezaba a hablar más tarde que
        // su propia marca provisional: la pestaña que había cedido el turno la
        // repetía. Se espera a que se libere ANTES de reclamar, no después.
        if (coordinar) await waitForSpeechIdle();
        if (debeCallarse()) break;
        const deferMs =
          coordinar && typeof document !== "undefined" && document.hidden
            ? HIDDEN_CLAIM_DELAY_MS
            : 0;
        // El turno se reserva por lo que va a durar ESTA frase, no por un plazo
        // fijo: una frase larga lo sobrevivía, y en un motor que no reporta el
        // arranque la marca no llegaba a pasar a firme antes de vencer.
        const ttlMs = speechClaimMs(item.text, rate, { earcon: earcons, alertType: item.type });
        if (coordinar && !(await claimAnnouncement(item.key, { deferMs, ttlMs }))) {
          // Otra pestaña pidió el turno, pero pedirlo no es haberlo usado: si
          // no llega a hablar, su reclamación vence en segundos. El item se
          // guarda para reintentarlo en vez de tirarse, que es lo que antes
          // dejaba un SOS sin decir en todo el puesto.
          //
          // Más de un intento porque el temporizador es UNO para toda la tanda:
          // una alerta cedida DESPUÉS de armarlo se reintenta antes de que
          // venza la reclamación que la dejó afuera, y con un solo intento esa
          // se perdía sin aparecer ni en el resumen.
          const intentos = item.intentos ?? 0;
          // Con el mismo tope que la cola, y por el mismo motivo: una ráfaga
          // larga contra una pestaña que gana todo dejaba esta lista creciendo
          // sin límite mientras el reintento espera su turno (ver `retryRef`).
          if (intentos < MAX_REINTENTOS && cedidosRef.current.length < MAX_QUEUE) {
            // Se guarda CUÁNDO vale la pena reintentar: la marca de la otra
            // pestaña dura lo que dure su frase, así que un plazo fijo dejaba
            // al reintento llegar temprano y gastar un intento contra una
            // reclamación todavía viva.
            cedidosRef.current.push({
              ...item,
              intentos: intentos + 1,
              reintentarDesde: Date.now() + ttlMs,
            });
          } else {
            // Se agotaron los intentos: la otra pestaña la está diciendo. No se
            // dice acá, pero se cuenta — perderla en silencio es lo único que
            // no puede pasar.
            anotarDescartada();
          }
          continue;
        }

        // A partir de acá el turno es de esta pestaña: cada salida que no
        // termine en voz tiene que soltarlo. Se ESPERA a que quede soltado: el
        // reintento de la otra pestaña lee el registro, y una escritura en
        // vuelo es una escritura que él todavía no ve.
        enVueloRef.current = coordinar ? item.key : null;
        if (debeCallarse()) {
          enVueloRef.current = null;
          if (coordinar) await releaseAnnouncement(item.key);
          break;
        }

        if (earcons) await playEarcon(item.type, volume);

        // Otro chequeo, despues del tono: apagar la voz mientras suena el
        // earcon no puede terminar con la frase locutada igual. `cancelSpeech()`
        // del efecto no sirve acá porque todavía no había nada que cancelar.
        if (debeCallarse()) {
          enVueloRef.current = null;
          if (coordinar) await releaseAnnouncement(item.key);
          break;
        }

        // La confirmación va atada al ARRANQUE de la voz, no a su final: una
        // frase larga dura más que la reclamación provisional, así que
        // confirmando al terminar la marca vencía con esta pestaña todavía
        // hablando y la que cedió el turno lo reintentaba ENCIMA. Y el
        // `false` del watchdog no significa "no habló": significa que `onend`
        // no llegó, que es otra cosa (ver `speech.js`).
        let arranco = false;
        // `cancelSpeech()` es global y lo llaman tres lugares: apagar la voz,
        // desmontar, y el cierre de la demo de la tarjeta de ajustes. La tanda
        // dice si alguno cortó ESTA frase mientras se esperaba.
        const tandaAlEmpezar = speechCancelEpoch();
        const termino = await speakText({
          text: item.text,
          voiceURI,
          rate,
          volume,
          // Misma regla que usa la app nativa para elegir canal de audio
          // (`AlertPresenter`): lo marcado como urgente va por el de alarma.
          urgent: isUrgentRef.current(item.type),
          // El tono va PEGADO a la frase y por el mismo canal de audio cuando
          // locuta el motor del teléfono; `playEarcon` de arriba se hace a un
          // lado ahí (ver `lib/earcons.js`). En web sigue siendo al revés: lo
          // tocó `playEarcon` y acá no hay nada que pedir.
          earcon: earcons,
          alertType: item.type,
          onStart: () => {
            arranco = true;
            if (coordinar) confirmAnnouncement(item.key);
          },
        });

        enVueloRef.current = null;
        // Una frase cancelada también dispara `onend`, aunque no haya sonado
        // nunca: quedaba confirmada como dicha y el puesto entero se la perdía
        // por dos minutos. Pasa de verdad — «Probar» en Ajustes encola la demo,
        // la alerta queda detrás, y salir de la pantalla cancela las dos.
        const cancelada = speechCancelEpoch() !== tandaAlEmpezar;
        if (cancelada) {
          // No sonó entera (o no sonó): el turno vuelve al puesto. Que otra
          // pestaña la repita es el final aceptable; que nadie la diga, no.
          //
          // Se cuenta, por lo mismo que la rama de abajo: soltar el turno no
          // sirve de nada si no hay otra pestaña —en la app directamente no hay
          // registro— y la alerta se perdía igual de callada que antes.
          //
          // Salvo que la haya callado el usuario. `cancelSpeech()` sale de tres
          // lugares y dos son deliberados: apagar el switch y desmontar. Contar
          // ahí hacía que, al volver a encender la voz, el puesto escuchara "y
          // una alerta más" por algo que él mismo mandó callar — y encima el
          // `droppedRef = 0` del efecto corre ANTES de que esta frase resuelva,
          // así que la cuenta quedaba viva.
          if (prefsRef.current.enabled && mountedRef.current) {
            noHablaron += 1;
            anotarDescartada();
          }
          if (coordinar) await releaseAnnouncement(item.key);
        } else if (arranco || termino) {
          hablo = true;
          // `onend` sin `onstart` es un motor que no reporta el arranque —pasa
          // en WebKit y en algunos Chrome de Android—: la frase SÍ salió, así
          // que el turno queda firme igual. Soltarlo acá era dejar que la
          // pestaña que cedió repitiera una alerta que el puesto ya escuchó.
          if (coordinar && !arranco) confirmAnnouncement(item.key);
        } else {
          // No arrancó ni terminó (audio bloqueado por la política de autoplay,
          // `onerror`, motor ausente). El turno vuelve al puesto por si otra
          // pestaña sí puede decirla, pero eso no alcanza: con UNA sola pestaña
          // —el caso normal— no hay a quién cederle el turno, y la alerta se
          // perdía entera. Se cuenta para que aparezca en el resumen: perderla
          // en silencio es lo único que no puede pasar.
          noHablaron += 1;
          anotarDescartada();
          if (coordinar) await releaseAnnouncement(item.key);
        }
      }

      const resumir =
        hablo && droppedRef.current > 0 && prefsRef.current.enabled && mountedRef.current;
      // Quién resume es quien ANUNCIA la ráfaga: la pestaña que no ganó ningún
      // turno no es esa, y la que sí lo ganó lleva su propia cuenta. Por eso el
      // contador se vacía al terminar la vuelta aunque no se haya dicho el
      // resumen — conservarlo dejaba a una pestaña perdedora acumulando durante
      // horas para soltar después un "y 37 alertas más" de alertas que la otra
      // ya anunció y ya resumió. La única excepción es que esta pestaña haya
      // quedado con algo para reintentar: ahí todavía puede ser la que anuncie.
      //
      // Una sola vez y en un solo lugar: la regla es una y escribirla dos veces
      // deja que un cambio en una de las condiciones contradiga a la otra.
      // El resumen también se reclama: el `hablo` de arriba solo calla a la
      // pestaña que no ganó NINGÚN turno, y en una ráfaga repartida entre dos
      // pestañas las dos ganan alguno y las dos decían "y N alertas más",
      // encimadas y con números distintos. Va por su propia clave fija con un
      // plazo propio: el resumen no es una alerta, así que ni se confirma ni se
      // suelta (ver `claimSummary`).
      const saleElResumen =
        resumir && (!needsCrossTabCoordination() || (await claimSummary()));
      // La cuenta se vacía cuando el resumen SALE, o cuando esta vuelta no dijo
      // nada Y no le quedó nada para reintentar: una pestaña que no ganó ningún
      // turno no puede acumular durante horas para soltar después un "y 37
      // alertas más" de alertas que la otra ya anunció y ya resumió.
      //
      // Lo que NO la vacía es perder la reclamación del resumen habiendo
      // hablado: esas alertas siguen sin decirse, y tirarlas acá era dejarlas
      // sin dicha, sin resumir y sin contar.
      // La cuenta se lee DESPUÉS de la reclamación, que es un await: lo que
      // entró mientras tanto entra en este mismo resumen en vez de quedar
      // colgado de un número viejo.
      const dropped = droppedRef.current;
      // `noHablaron` son alertas cuyo turno ganó ESTA pestaña y que igual no
      // sonaron: no son de nadie más, así que vaciar la cuenta acá las borraría
      // del único lado donde quedaron anotadas.
      if (saleElResumen || (!hablo && cedidosRef.current.length === 0 && noHablaron === 0)) {
        // Se resta lo resumido en vez de poner la cuenta en cero, por si algún
        // día entra una descartada entre la lectura de `dropped` y esta línea.
        // Hoy no puede: las dos están del mismo lado del último `await`, así
        // que la resta siempre da cero — el día que alguien mueva la lectura
        // arriba del await, esto ya está listo y el instante también.
        droppedRef.current = Math.max(droppedRef.current - dropped, 0);
        // Con la cuenta vacía, la próxima descartada abre tanda y fija su
        // instante. Si quedara un residuo, conserva el de la tanda resumida —
        // el más viejo—, que es lo prudente: caduca antes, no después.
        if (droppedRef.current === 0) droppedAtRef.current = 0;
      }
      // Se vuelve a mirar el switch: `claimSummary` es un await, y apagar la voz
      // ahí en el medio tiene que callar esto igual que a cualquier otra frase.
      if (saleElResumen && dropped > 0 && !debeCallarse()) {
        await speakText({
          text: dropped === 1 ? "y una alerta más" : `y ${dropped} alertas más`,
          voiceURI: prefsRef.current.voiceURI,
          rate: prefsRef.current.rate,
          volume: prefsRef.current.volume,
          // No urgente, aunque la ráfaga lo haya sido: el default de `speakText`
          // es urgente y eso mandaba "y 4 alertas más" por el canal de ALARMA
          // —el que suena con el teléfono en silencio de noche— para decir un
          // número. Las alertas de la ráfaga ya salieron cada una por el canal
          // que le tocaba.
          urgent: false,
        });
      }
    } finally {
      draining.current = false;
      // Puede haber entrado algo mientras se locutaba la última: sin este
      // reintento la cola se quedaría dormida hasta la alerta siguiente.
      if (queueRef.current.length > 0 && prefsRef.current.enabled) drain();
      else if (cedidosRef.current.length > 0 && !retryRef.current) {
        // Las que se llevó otra pestaña, cuando ya venció su reclamación
        // provisional. No se reclama acá: se devuelven a la cola y
        // `drain` decide — si la otra pestaña sí habló, su reclamación quedó
        // firme, la reclamación falla y el item se descarta sin ruido.
        // La espera la fija la reclamación más larga que dejó algo afuera.
        const reintentarDesde = Math.max(
          ...cedidosRef.current.map((i) => i.reintentarDesde ?? 0),
          Date.now() + PROVISIONAL_TTL_MS,
        );
        retryRef.current = setTimeout(() => {
          retryRef.current = null;
          const pendientes = cedidosRef.current;
          cedidosRef.current = [];
          // Condición explícita y no `debeCallarse()`: aquél VACÍA la cola como
          // efecto secundario, y acá solo se quiere la respuesta. Que un
          // cortocircuito booleano decida si se tira lo pendiente es una trampa
          // para el próximo que toque esta línea.
          if (!prefsRef.current.enabled || !mountedRef.current) return;
          if (pendientes.length === 0) return;

          // Los lugares que quedan LIBRES, no `MAX_QUEUE` a secas: entre que
          // se armó este temporizador y que disparó pudo entrar una ráfaga
          // nueva, y sumarle encima otras cuatro dejaba la cola al doble del
          // tope que existe justamente para no hablar 24 s seguidos de eventos
          // ya viejos.
          const libres = Math.max(MAX_QUEUE - queueRef.current.length, 0);
          queueRef.current.push(...pendientes.slice(0, libres));
          // El excedente se cuenta, no se tira: si no, esas alertas no las dice
          // ni las resume nadie. Es el mismo criterio que el desborde de
          // `handleMessage`.
          const excedente = pendientes.length - libres;
          if (excedente > 0) anotarDescartada(excedente);
          drain();
        }, reintentarDesde - Date.now() + RETRY_SLACK_MS);
      }
    }
  }, [debeCallarse, anotarDescartada, caducarDescartadas]);

  const handleMessage = useCallback(
    (msg) => {
      if (msg?.type !== "alert") return;
      const { enabled } = prefsRef.current;
      if (!enabled || !isSpeechSupported()) return;

      // Reenvío del mismo id (resync tras reconexión): no locutar dos veces.
      if (msg.id != null) {
        if (seenIdsRef.current.has(msg.id)) return;
        seenIdsRef.current.add(msg.id);
        if (seenIdsRef.current.size > MAX_COOLDOWN_ENTRIES) {
          seenIdsRef.current = new Set([msg.id]);
        }
      }

      if (!isTypeAnnounced(prefsRef.current, msg.alert_type)) return;

      const key = `${msg.imei}:${msg.alert_type}`;
      const now = Date.now();
      const last = lastSpokenRef.current.get(key);
      if (last != null && now - last < REPEAT_COOLDOWN_MS) return;

      // Con la cola llena la alerta NO se locuta: solo suma al resumen. El
      // cooldown se registra únicamente cuando la alerta entra a la cola — si se
      // marcara acá, una alerta descartada en una ráfaga silenciaría durante un
      // minuto a las repeticiones siguientes de ese mismo (vehículo, tipo), que
      // es justo lo contrario de lo que el cooldown busca: un SOS repetido
      // quedaría mudo por culpa de un SOS que nunca llegó a sonar.
      if (queueRef.current.length >= MAX_QUEUE) {
        anotarDescartada();
        drain();
        return;
      }

      if (lastSpokenRef.current.size >= MAX_COOLDOWN_ENTRIES) lastSpokenRef.current.clear();
      lastSpokenRef.current.set(key, now);

      queueRef.current.push({
        text: alertSpeech({
          type: msg.alert_type,
          plate: msg.plate,
          imei: msg.imei,
          // Conductor asignado al momento de la alerta, tal como lo resolvió el
          // backend. NO se busca en el store de flota: este hook está montado en
          // toda la app y en la mayoría de las pantallas el store ni se cargó.
          driverFirstName: msg.driver_first_name,
          driverLastName: msg.driver_last_name,
          payload: msg.payload,
          phonetic: prefsRef.current.phonetic,
          identify: prefsRef.current.identify,
        }),
        type: msg.alert_type,
        // Cómo reconocen esta misma alerta las otras pestañas del puesto.
        key: announcementKey(msg),
      });

      drain();
    },
    [drain, anotarDescartada],
  );

  useWebSocket(handleMessage);

  // Con la voz activada, cualquier gesto del usuario en la app desbloquea el
  // audio de ESTA carga de página. Sin esto la locución solo funcionaba en la
  // sesión en que se encendió el switch — ver useAudioUnlock.
  useAudioUnlock(prefs.enabled);

  // Las preferencias se copian al motor nativo cada vez que cambian. Este hook
  // habla con la app ABIERTA; con la app cerrada locuta `AlertPresenter`, que no
  // tiene forma de leer el localStorage de este WebView —ni siquiera está
  // corriendo— así que sin la copia la configuración quedaba sin efecto justo
  // con el teléfono bloqueado. En web es un no-op.
  //
  // Va acá y no en la tarjeta de ajustes porque este hook está montado en toda
  // la app: una instalación nueva queda sincronizada al primer arranque, sin
  // que el usuario entre nunca a Preferencias. Y la lista por tipo se toca en
  // OTRA pantalla (Alertas), que ni siquiera monta la tarjeta.
  //
  // La dependencia es `prefs` entero y no campo por campo: republicar de más es
  // un `apply()` sobre SharedPreferences con los mismos valores, y filtrar por
  // campo obligaría a serializar el mapa de tipos para tener una dependencia
  // estable (`prefs.types` cambia de identidad ante cualquier cambio de
  // preferencias). El volumen —el único control que se arrastra— no existe en
  // la app, así que no hay ráfaga que evitar.
  //
  // Lo único que NO viaja es el volumen: en nativo lo pone el sistema, a
  // propósito (lo urgente sale por el canal de alarma). El tono y la voz
  // elegida SÍ viajan desde que los toca el motor del teléfono — mientras los
  // tocaba este WebView no tenía sentido copiarlos, porque con la app cerrada
  // este WebView no existe.
  useEffect(() => {
    publishVoicePreferences({
      enabled: prefs.enabled,
      identify: prefs.identify,
      phonetic: prefs.phonetic,
      rate: prefs.rate,
      earcons: prefs.earcons,
      voiceURI: prefs.voiceURI,
      types: prefs.types,
    });
  }, [prefs]);

  // Apagar la voz corta la locución en curso: esperar a que termine la frase
  // después de haber apretado el switch se siente como que no funcionó.
  useEffect(() => {
    if (!prefs.enabled) {
      queueRef.current = [];
      cedidosRef.current = [];
      clearTimeout(retryRef.current);
      retryRef.current = null;
      droppedRef.current = 0;
      cancelSpeech();
    }
  }, [prefs.enabled]);

  // Cerrar la pestaña en mitad de una frase dejaba esa alerta marcada como
  // dicha por dos minutos, y ninguna otra pestaña la repetía: el puesto se
  // quedaba con media palabra de un SOS. La confirmación va atada al ARRANQUE
  // de la voz (ver el drain), así que este es el único lugar donde se puede
  // deshacer. `pagehide` es el último momento en que el navegador deja
  // escribir, y ahí no se puede esperar una promesa — por eso el registro se
  // toca en su versión síncrona.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const soltar = () => {
      // También se suelta al entrar al bfcache (`event.persisted`), aunque la
      // página vuelva: al congelarla el navegador para el sintetizador, así que
      // la frase NO sigue al volver — la utterance queda muerta. Conservando el
      // turno ahí, la alerta se cortaba en la primera sílaba y ninguna pestaña
      // la repetía.
      releaseAnnouncementNow(enVueloRef.current);
      enVueloRef.current = null;
    };
    window.addEventListener("pagehide", soltar);
    return () => window.removeEventListener("pagehide", soltar);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queueRef.current = [];
      cedidosRef.current = [];
      clearTimeout(retryRef.current);
      retryRef.current = null;
      // Desmontar es cortar la frase en curso: el turno vuelve al puesto, igual
      // que al cerrar la pestaña.
      releaseAnnouncementNow(enVueloRef.current);
      enVueloRef.current = null;
      cancelSpeech();
    };
  }, []);
}
